/**
 * Where the call goes in and where it comes out.
 *
 * The first thing that breaks at the start of a day and the last thing the CRM
 * used to have an answer for: Chrome picks up the laptop's built-in microphone
 * after a headset was replugged, or sends the call to a monitor's speakers, and
 * until this panel existed the only way to fix either was Chrome's own site
 * settings - three menus deep, on the far side of the browser from the call that
 * is going wrong.
 *
 * Three choices rather than the two the plan asked for. The ring is separate
 * from the call on purpose: an operator wearing a headset who only hears the
 * phone INSIDE it misses every call they stand up during, so ringing the room
 * and talking into the headset has to be expressible. That is also the normal
 * arrangement at a desk, not an edge case.
 *
 * THREE FOLDED ROWS, NOT THREE OPEN LISTS. A desk with a headset, a monitor and
 * two virtual drivers on it enumerates seven or eight devices per kind, so drawn
 * as plain lists this panel was over a thousand pixels tall: inside the call
 * window it pushed the hang-up button off the bottom of the screen and left the
 * AI column beside it half empty, and inside the dial pad's dropdown it ran off
 * the end of the display. Folded, it is three lines that say what is in use, and
 * the list only appears for the one kind being changed.
 *
 * The fold also settles the panel. Every one of those names arrives LATE - the
 * browser withholds device labels until the microphone has been granted once,
 * which is what the meter below asks for - so an open list grew by a few hundred
 * pixels a second or two after opening, and a dropdown anchored to a button
 * jumped every time it did. A folded row changes its text and keeps its height.
 *
 * The panel is rendered in two places and knows the difference through one prop.
 * `liveAudio` is Twilio's routing for a call that is UP; it is null in the dial
 * pad, and everything downstream reads that as "store the choice, apply it when
 * the call starts". See `apply.ts` for why the microphone is not applied any
 * earlier than that.
 *
 * NOTHING HEAVY HAPPENS IN THE FIRST QUARTER SECOND. Both places this panel
 * lives are dropdowns that mount it at the moment they open, so everything it
 * does on mount runs underneath its own entrance animation - and what it does
 * on mount is open a microphone. `getUserMedia` is not a cheap call: it wakes
 * the OS audio device, and on this machine it holds the main thread long enough
 * to eat several frames of a 220ms animation. The menu jerked its way open
 * every single time, which reads as "something is still loading" because
 * something is. See `ENTRANCE_MS`.
 *
 * EACH ROW ALSO CARRIES A LEVEL. "Which device" and "how loud" are the two
 * halves of the same complaint and they arrive together - an operator who has
 * just found out the call is coming out of the wrong speaker is one keystroke
 * away from finding out it is also too quiet - so they are one line apart
 * rather than one screen apart. The three levels do not share a mechanism:
 * `gain.ts` fits a gain node to the microphone, `speaker.ts` turns the SDK's
 * own audio elements, and the ring is our own synthesiser. Each file says why.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Mic, Play, Speaker, Volume2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { applyAudioInput, applyAudioOutputs, type CallAudio } from "./apply";
import {
  audioDeviceLabel,
  chosenDeviceLabel,
  detachedAudioKinds,
  effectiveAudioChoice,
  readAudioChoice,
  writeAudioChoice,
  type AudioChoice,
  type AudioKind,
} from "./choice";
import { audioContextState, ringGate, type RingGate } from "./context";
import { DeviceRow } from "./DeviceRow";
import { applyMicGain } from "./gain";
import { Meter } from "./Meter";
import { setSpeakerLevel } from "./speaker";
import { playTestTone } from "./tone";
import { useAudioDevices } from "./useAudioDevices";
import { useInputLevel } from "./useInputLevel";
import { VolumeSlider } from "./VolumeSlider";
import {
  AUDIO_VOLUME_MAX,
  clampVolume,
  readAudioVolume,
  writeAudioVolume,
  type AudioVolume,
} from "./volume";

/**
 * What to say about each way the ring stays silent.
 *
 * A lookup rather than a template string, so a new `RingGate` value cannot ship
 * with a missing translation - `catalogues.test.ts` compares key sets, and a key
 * built at runtime is invisible to it.
 */
const RING_GATE_KEY: Record<Exclude<RingGate, "ready">, string> = {
  muted: "calls.audio.ringMuted",
  "reduced-motion": "calls.audio.ringReducedMotion",
  locked: "calls.audio.ringLocked",
};

/** Heading, icon and the device kind each row is drawn from. */
const SECTIONS: { kind: AudioKind; label: string; icon: typeof Mic; from: "inputs" | "outputs" }[] = [
  { kind: "input", label: "calls.audio.mic", icon: Mic, from: "inputs" },
  { kind: "output", label: "calls.audio.speaker", icon: Speaker, from: "outputs" },
  { kind: "ring", label: "calls.audio.ring", icon: Volume2, from: "outputs" },
];

/**
 * How long the panel keeps its hands off the microphone after it mounts.
 *
 * The dropdown's entrance (`crm-pop` in `index.css`) is 220ms, and this is that
 * plus a frame. It is a delay chosen to be UNDER the threshold where a bar that
 * has not started yet reads as broken, and over the length of the animation it
 * would otherwise trample: the panel draws its three rows immediately, and the
 * only thing that waits is the meter beneath the first one.
 *
 * ⚠ TIED TO THE CSS. Shorten the animation and this is longer than it needs to
 * be; lengthen it and the stutter comes back.
 */
const ENTRANCE_MS = 260;

export function AudioPicker({
  liveAudio,
  capture = true,
}: {
  liveAudio: CallAudio | null;
  /**
   * May the panel open a microphone of its own to draw the level?
   *
   * True in the two dropdowns, which are on screen for seconds at a time. False
   * in the call rail, where the panel sits inside a section an operator can
   * leave open all day - a capture that lives that long holds the device and
   * lights the tab's recording dot for a bar nobody is watching. It costs
   * nothing during a call either way: the level then comes from Twilio's
   * reading of the stream already going out, not from a second capture.
   */
  capture?: boolean;
}) {
  const t = useT();
  const devices = useAudioDevices();
  const [wanted, setWanted] = useState<AudioChoice>(readAudioChoice);
  /**
   * The three levels, held here and written straight through to storage.
   *
   * Local state rather than a read of storage per render, because the slider
   * has to move under the pointer at the frame rate of the drag - and unlike
   * the device choice, which changes once, this one changes forty times per
   * gesture.
   */
  const [volume, setVolume] = useState<AudioVolume>(readAudioVolume);
  const [testing, setTesting] = useState<AudioKind | null>(null);
  /**
   * Which of the three lists is unfolded, or none.
   *
   * One slot rather than three booleans, and that is a size decision as much as
   * a tidiness one: three lists open at once is the tall panel this fold exists
   * to get rid of.
   */
  const [unfolded, setUnfolded] = useState<AudioKind | null>(null);
  /** The entrance animation has played out and the panel is standing still. */
  const [settled, setSettled] = useState(false);
  /**
   * The operating system's "do not animate" setting, which `useRingtone` reads
   * as "do not make a noise at me either".
   *
   * Read once and not watched: it is a control-panel setting, and an operator
   * who changes it mid-session has a whole browser to reload. Read at all
   * because it is the one reason for a silent phone that leaves no trace
   * anywhere in this app - no slider is down, no device is missing.
   */
  const [reducedMotion] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true,
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), ENTRANCE_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const effective = effectiveAudioChoice(wanted, devices.attached);
  const detached = new Set(detachedAudioKinds(wanted, devices.attached, devices.ready));
  // Two gates, and the second is not about smoothness. Until the browser has
  // answered `enumerateDevices` nothing is attached as far as this panel knows,
  // so `effective.input` is "" - and a capture opened on that is a capture of
  // the system default, torn down and opened again on the operator's actual
  // headset a tick later. Two device opens where one was asked for.
  /**
   * Is there a level to read at all?
   *
   * The rail's `capture={false}` says "not from a microphone of your own", and
   * with no call up that leaves nothing - so the bar is not drawn rather than
   * drawn flat. Deliberately NOT folded in with `settled` below: that one is a
   * wait of a quarter second, and a note that appeared for four frames before
   * a meter replaced it would be a flicker on every dropdown that opens.
   */
  const hasLevel = capture || liveAudio !== null;
  const meter = useInputLevel({
    active: settled && devices.ready && hasLevel,
    deviceId: effective.input,
    audio: liveAudio,
  });

  // The browser withholds device NAMES until the microphone has been granted
  // once, and the meter above is what asks for it. So the moment it starts
  // reading, the lists are worth fetching again - that is the difference
  // between three blanks and three headsets.
  const { refresh, named } = devices;
  useEffect(() => {
    if (meter.state === "live" && !named) refresh();
  }, [meter.state, named, refresh]);

  const choose = useCallback(
    (kind: AudioKind, deviceId: string) => {
      const next = { ...wanted, [kind]: deviceId };
      setWanted(next);
      writeAudioChoice(next);
      // Folded again on the way out. A combobox that stayed open after a pick
      // would leave the operator reading a list to find out what they had just
      // chosen, when the row above it now says so in one line.
      setUnfolded(null);
      // Only ever onto a call that is already up. With no call there is nothing
      // to route, and the choice is read back from storage when one starts.
      const now = effectiveAudioChoice(next, devices.attached);
      if (kind === "input") void applyAudioInput(liveAudio, now);
      if (kind === "output") void applyAudioOutputs(liveAudio, now);
      // The ring needs nothing here: `useRingtone` reads the stored choice when
      // the phone next rings, and it is not ringing while this panel is open.
    },
    [wanted, devices.attached, liveAudio],
  );

  /**
   * A level moved.
   *
   * Applied immediately and to the LIVE call, not stored for the next one:
   * every one of these three is dragged while somebody is on the line saying
   * they cannot hear, or while the phone is ringing too quietly to be heard
   * from the next room. The ring is the exception with no line here, and it is
   * not an omission - `useRingtone` re-reads the level on every burst, so a
   * ring already in progress follows the slider without this file knowing.
   */
  const changeVolume = useCallback(
    (kind: AudioKind, value: number) => {
      const next = { ...volume, [kind]: clampVolume(kind, value) };
      setVolume(next);
      writeAudioVolume(next);
      if (kind === "output") setSpeakerLevel(next.output);
      // Null while no call is up, which `applyMicGain` reads as "store it": the
      // level goes on at the moment the call is placed, exactly like the device.
      if (kind === "input") void applyMicGain(liveAudio, next.input);
    },
    [volume, liveAudio],
  );

  const test = useCallback(
    (kind: AudioKind, deviceId: string) => {
      setTesting(kind);
      // At the level chosen right above the button. A test tone played at a
      // different volume from the thing being tested answers nothing.
      void playTestTone(deviceId, volume[kind]).finally(() => setTesting(null));
    },
    [volume],
  );

  /**
   * Would the phone actually make a sound if it rang right now?
   *
   * Drawn ABOVE the sections and in the unsupported branch too, because every
   * answer but "ready" is a phone that is silent for a reason no operator can
   * work out by looking at a device list - and the rail this panel sits in is
   * the one place it gets read before the day starts rather than after a call
   * has already been missed.
   *
   * Read during render rather than watched. The two answers that persist -
   * the level at zero and the reduced-motion preference - are already state
   * this component re-renders on, and the third cannot survive the panel being
   * open: opening it is a click, and a click is what unlocks the context.
   */
  const gate = ringGate({ state: audioContextState(), volume: volume.ring, reducedMotion });
  const gateNote: string | null = gate === "ready" ? null : t(RING_GATE_KEY[gate]);

  if (!devices.supported) {
    return (
      <div className="crm-audio">
        {gateNote ? <p className="crm-audio-note">{gateNote}</p> : null}
        <p className="crm-audio-note">{t("calls.audio.unsupported")}</p>
      </div>
    );
  }

  return (
    <div className="crm-audio">
      {gateNote ? <p className="crm-audio-note">{gateNote}</p> : null}
      {SECTIONS.map((section) => {
        const list = section.from === "inputs" ? devices.inputs : devices.outputs;
        // Output selection is a browser capability, not a device one: Safari
        // enumerates speakers it will not let anybody choose between.
        const frozen = section.kind !== "input" && !devices.routable;
        const chosen = wanted[section.kind];
        const open = unfolded === section.kind;
        const current = chosenDeviceLabel(
          chosen,
          list,
          {
            systemDefault: t("calls.audio.systemDefault"),
            gone: t("calls.audio.gone"),
            pending: t("calls.audio.pending"),
            unnamed: (n) => t("calls.audio.unnamed", { i: n }),
          },
          devices.ready,
        );

        if (frozen) {
          return (
            <section key={section.kind} className="crm-audio-sec">
              <div className="crm-audio-head">
                <section.icon className="size-3.5" />
                {t(section.label)}
              </div>
              <p className="crm-audio-note">{t("calls.audio.noRouting")}</p>
              {/* The ring keeps its level even here, and the speaker does not.
                  It is not an inconsistency: the ring is a sound this app
                  synthesises through a gain node of its own, so it can be turned
                  wherever it comes out - while the speaker level is set on the
                  SDK's audio element, and the browsers that cannot route audio
                  are exactly the ones whose element the SDK never hands over
                  (see `speaker.ts`). Drawing a speaker slider that did nothing
                  would be worse than drawing none. */}
              {section.kind === "ring" ? (
                <VolumeSlider
                  value={volume.ring}
                  max={AUDIO_VOLUME_MAX.ring}
                  label={t(section.label)}
                  onChange={(v) => changeVolume("ring", v)}
                />
              ) : null}
            </section>
          );
        }

        return (
          <section key={section.kind} className={cn("crm-audio-sec", open && "is-open")}>
            <button
              type="button"
              className="crm-audio-row"
              aria-expanded={open}
              aria-haspopup="listbox"
              onClick={() => setUnfolded((cur) => (cur === section.kind ? null : section.kind))}
            >
              <section.icon className="crm-audio-ic size-4" />
              <span className="crm-audio-txt">
                <span className="crm-audio-lb">{t(section.label)}</span>
                {/* The device is chosen and not here. Said in the row itself
                    rather than quietly corrected: "the call is coming out of
                    somewhere other than where you chose" is the exact state this
                    panel exists to make visible, and the folded row is all that
                    is on screen most of the time. */}
                {/* `crm-mq` for the same reason the list rows below carry it:
                    in the rail this line is under 150px wide, and a device name
                    is not a sentence whose first words are the information.
                    Resting the pointer on the fold reads it out to the end. The
                    outer element keeps the state colours; the inner one is the
                    part that slides. */}
                <span
                  className={cn(
                    "crm-audio-val crm-mq",
                    current.state === "gone" && "is-gone",
                    current.state === "pending" && "is-pending",
                  )}
                >
                  <span>{current.label}</span>
                </span>
              </span>
              <ChevronDown className={cn("crm-audio-chev size-4", open && "is-open")} />
            </button>

            {/* A flat bar that will never move is worse than no bar: it reads
                as a microphone that is hearing nothing, which is the exact
                fault this panel is opened to diagnose. Where nothing is being
                read, the row says where the level IS shown instead. */}
            {section.kind === "input" ? (
              hasLevel ? (
                <Meter barRef={meter.barRef} state={meter.state} />
              ) : (
                <p className="crm-audio-note">{t("calls.audio.meterElsewhere")}</p>
              )
            ) : null}

            {/* Under the meter on the microphone row, and that order is the
                point: the bar answers "is it hearing me", the slider answers
                "loudly enough", and during a call the bar is reading the
                PROCESSED stream - so dragging this moves the bar above it and
                the operator can see the level they are choosing. */}
            <VolumeSlider
              value={volume[section.kind]}
              max={AUDIO_VOLUME_MAX[section.kind]}
              label={t(section.label)}
              onChange={(v) => changeVolume(section.kind, v)}
            />

            {detached.has(section.kind) ? <p className="crm-audio-note">{t("calls.audio.detached")}</p> : null}

            {open ? (
              <>
                <div className="crm-audio-list" role="listbox" aria-label={t(section.label)}>
                  <DeviceRow
                    label={t("calls.audio.systemDefault")}
                    chosen={chosen === ""}
                    onPick={() => choose(section.kind, "")}
                  />
                  {list.map((device, i) => (
                    <DeviceRow
                      key={device.deviceId}
                      label={audioDeviceLabel(device, i, (n) => t("calls.audio.unnamed", { i: n }))}
                      chosen={chosen === device.deviceId}
                      onPick={() => choose(section.kind, device.deviceId)}
                    />
                  ))}
                </div>
                {/* BESIDE the list, not inside it, and that is not only tidiness.
                    The list is a `role="listbox"` and a scroll box: a button
                    among the options is a row a screen reader has to announce as
                    an eighth device, and in the rail - where the list is capped
                    at a few rows - it scrolled out of sight the moment there was
                    anything worth scrolling past. It belongs to the KIND, like
                    the slider above it, so it sits with the section and stays
                    put while the devices move under it. */}
                {section.kind !== "input" ? (
                  <button
                    type="button"
                    className="crm-audio-test"
                    onClick={() => test(section.kind, effective[section.kind])}
                    disabled={testing !== null}
                  >
                    <Play className="size-3.5" />
                    {t(testing === section.kind ? "calls.audio.testing" : "calls.audio.test")}
                  </button>
                ) : null}
              </>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
